import React from "react";
import { Link } from "react-router-dom";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import { makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Divider from "@material-ui/core/Divider";
import ListSubheader from "@material-ui/core/ListSubheader";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import ListItemText from "@material-ui/core/ListItemText";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import MenuIcon from "@material-ui/icons/Menu";
import ImageIcon from "@material-ui/icons/Image";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import clsx from "clsx";

import { LayoutPortalState } from "./LayoutPortalContext";
import egisWhiteLogo from "../../assets/logo/egisWhiteLogo.png";
import LayoutPortalProvider from "./LayoutPortalProvider";
import LocaleSwitcher from "../LocaleSwitcher";
import ResponsiveDrawer, { drawerWidth } from "./ResponsiveDrawer";
import useAuth from "../../hooks/useAuth";
import { version } from "../../../package.json";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
    height: "100%",
    overflow: "hidden",
  },
  appBar: {
    height: 46,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  toolbar: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: 46,
  },
  toolbarPiece: {
    height: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  title: {
    "& >:first-child": {
      fontWeight: theme.typography.fontWeightRegular,
    },
    "& >:nth-child(2)": {
      fontWeight: theme.typography.fontWeightMedium,
    },
    "& >:nth-child(3)": {
      ...theme.typography.caption,
      fontWeight: theme.typography.fontWeightLight,
    },
  },
  portalContainer: {},
  localeSwitcher: {
    marginLeft: 8,
    padding: 4, // Because inner icon is 24x24px and toolbar is 46px tall.
  },
  openMainMenuButton: {
    marginRight: 8,
    padding: 4, // Because inner icon is 24x24px and toolbar is 46px tall.
  },
  closeMainMenuButton: {
    padding: 4, // Because inner icon is 24x24px and toolbar is 46px tall.
  },
  logoutButton: {
    marginLeft: 8,
    padding: 4, // Because inner icon is 24x24px and toolbar is 46px tall.
  },
  hide: {
    display: "none",
  },
  drawerHeader: {
    height: 46,
    display: "flex",
    justifyContent: theme.direction === "ltr" ? "flex-end" : "flex-start",
    alignItems: "center",
    padding: theme.spacing(0, 1),
  },
  content: {
    width: "100%",
    height: "calc(100% - 46px)",
    marginLeft: 0,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflow: "auto",
  },
  contentShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  logo: {
    width: 100,
    marginRight: 12,
  },
}));

const msg = defineMessages({
  navMenuTitle: {
    id: "layout.navMenu.title",
    description: "Title of the navigation menu.",
    defaultMessage: "Navigation menu",
  },
  logoutButtonTooltipLabel: {
    id: "layout.logoutButtonTooltip.label",
    description: "Label of the logout button tooltip.",
    defaultMessage: "Log out",
  },
  openMainMenuButtonTooltipLabel: {
    id: "layout.openMainMenuButtonTooltip.label",
    description: "Label of the open main menu button tooltip.",
    defaultMessage: "Open the main menu",
  },
  closeMainMenuButtonTooltipLabel: {
    id: "layout.closeMainMenuButtonTooltip.label",
    description: "Label of the close main menu button tooltip.",
    defaultMessage: "Close the main menu",
  },
});

export type LayoutProps = React.PropsWithChildren<{
  /**
   * If `true`, the drawer will be hidden.
   */
  hideDrawer?: boolean;
  /**
   * If `true`, the locale switcher will be hidden.
   */
  hideLocaleSwitcher?: boolean;
}>;

function Layout(props: LayoutProps) {
  ///////////////// PROPS, HOOKS AND STATE SETUP////////////////////////////////

  const { children, hideDrawer = false, hideLocaleSwitcher = false } = props;
  const { formatMessage: t } = useIntl();
  const { loggedIn, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const leftContainer = React.useRef<HTMLDivElement>(null);
  const rightContainer = React.useRef<HTMLDivElement>(null);

  ///////////////// CSS ////////////////////////////////////////////////////////

  const classes = useStyles();
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));
  const appBarClasses = React.useMemo(() => {
    return clsx(classes.appBar, smUp && drawerOpen && classes.appBarShift);
  }, [classes.appBar, classes.appBarShift, drawerOpen, smUp]);
  const contentClasses = React.useMemo(() => {
    return clsx(classes.content, smUp && drawerOpen && classes.contentShift);
  }, [classes.content, classes.contentShift, drawerOpen, smUp]);
  const openMainMenuButtonClasses = React.useMemo(() => {
    return clsx(classes.openMainMenuButton, smUp && drawerOpen && classes.hide);
  }, [classes.hide, classes.openMainMenuButton, drawerOpen, smUp]);

  ///////////////// INIT AND SIDE EFFECT(S) ////////////////////////////////////

  const layoutPortalState: LayoutPortalState = React.useMemo(() => {
    return { leftContainer, rightContainer };
  }, []);

  ///////////////// WORKFLOW ///////////////////////////////////////////////////

  const handleDrawerOpen = React.useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = React.useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleLogoutButtonClick = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      logout("hard");
    },
    [logout]
  );

  return (
    <div className={classes.root}>
      <AppBar className={appBarClasses} position="static">
        <Toolbar className={classes.toolbar}>
          <div className={classes.toolbarPiece}>
            {!hideDrawer && (
              <Tooltip title={t(msg.openMainMenuButtonTooltipLabel)}>
                <span>
                  <IconButton
                    className={openMainMenuButtonClasses}
                    color="inherit"
                    edge="start"
                    aria-label={t(msg.openMainMenuButtonTooltipLabel)}
                    disableRipple
                    onClick={handleDrawerOpen}
                  >
                    <MenuIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {loggedIn ? (
              <Typography className={classes.title} variant="h6" noWrap>
                <span>Image</span>&nbsp;
                <span>Review</span>&nbsp;
                <span>
                  {version} {process.env.REACT_APP_VERSION_CODENAME}
                </span>
              </Typography>
            ) : (
              <img
                className={classes.logo}
                src={egisWhiteLogo}
                alt="egis logo"
              />
            )}
          </div>
          <div className={classes.portalContainer} ref={leftContainer} />
          <div className={classes.toolbarPiece}>
            <div className={classes.portalContainer} ref={rightContainer} />
            {!hideLocaleSwitcher && (
              <LocaleSwitcher
                className={classes.localeSwitcher}
                color="inherit"
              />
            )}
            {loggedIn && (
              <Tooltip title={t(msg.logoutButtonTooltipLabel)}>
                <IconButton
                  className={classes.logoutButton}
                  aria-label={t(msg.logoutButtonTooltipLabel)}
                  color="inherit"
                  onClick={handleLogoutButtonClick}
                >
                  <PowerSettingsNewIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </Toolbar>
      </AppBar>
      {!hideDrawer && (
        <ResponsiveDrawer open={drawerOpen} onClose={handleDrawerClose}>
          <div className={classes.drawerHeader}>
            <Tooltip title={t(msg.closeMainMenuButtonTooltipLabel)}>
              <IconButton
                className={classes.closeMainMenuButton}
                aria-label={t(msg.closeMainMenuButtonTooltipLabel)}
                disableRipple
                onClick={handleDrawerClose}
              >
                {theme.direction === "ltr" ? (
                  <ChevronLeftIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            </Tooltip>
          </div>
          <Divider />
          <List
            component="nav"
            aria-labelledby="navMenuTitle"
            subheader={
              <ListSubheader component="div" id="navMenuTitle">
                <FormattedMessage {...msg.navMenuTitle} />
              </ListSubheader>
            }
          >
            <ListItem
              button
              disableRipple={!smUp}
              onClick={!smUp ? handleDrawerClose : undefined}
              component={Link}
              to="/"
            >
              <ListItemIcon>
                <ImageIcon />
              </ListItemIcon>
              <ListItemText>{document.title}</ListItemText>
            </ListItem>
          </List>
        </ResponsiveDrawer>
      )}
      <LayoutPortalProvider value={layoutPortalState}>
        <main className={contentClasses}>{children}</main>
      </LayoutPortalProvider>
    </div>
  );
}

export default Layout;
